/* React */
import React, {
  useRef,
  useEffect,
  useMemo,
  useId,
} from "react";

/* React Redux */
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  expandGroupedNodes,
  toggleTreatmentGroup,
} from "src/redux/features/insights-options/treatment-patterns/sankey-diagram/sankeyDiagramOptionsSlice";

/* d3.js */
import * as d3 from "d3";
import { sankey, SankeyNode, SankeyLink } from "d3-sankey";

/* Utils */
import { createSankeyDiagram } from "src/components/charts/sankey-diagram/sankeyDiagramRenderer";
import { useClickOutsideChartHandler } from "src/utils/hooks/useClickOutsideChartHandler";
import {
  groupSmallNodesToOther,
} from "src/utils/helper/treatment-patterns/transformTreatmentData";
import { applyTreatmentGroupMapping, resolveGroupNodeName } from "src/utils/helper/treatment-patterns/helper";

/* Config */
import { TREATMENT_GROUP_MAP } from "src/config/treatmentGroupMap";

/* Components */
import ChartTooltip from "src/components/shared/chart-tooltip/ChartTooltip";

/* Interfaces */
import { ChartTooltipRef } from "src/shared/interfaces/ChartTooltip";
import { SankeyDiagramProps } from "src/shared/interfaces/charts/SankeyDiagram";
import { ChartDimensionsProps } from "src/shared/interfaces/charts/BaseChart";

const SankeyDiagram: React.FC<{
  data: SankeyDiagramProps;
  originalData: SankeyDiagramProps;
  dimension: ChartDimensionsProps;
}> = ({ data, originalData, dimension }) => {
  const dispatch = useDispatch();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<ChartTooltipRef>(null);

  const insightsFilterState = useSelector(
    (state: RootState) => state.insightsFilter
  );

  const { customFeature } = insightsFilterState;

  useClickOutsideChartHandler(svgRef);

  /**
   * Create a unique ID to handle each diagram
   * which is rendered in the DOM individually.
   * Otherwise, `useClickOutsideChartHandler`
   * and the gradient will not work properly.
   */
  const uniqueId = useId();
  const diagramId = useMemo(() => {
    return `sankey-${uniqueId.replace(
      /[^a-zA-Z0-9-_]/g,
      ""
    )}`;
  }, [uniqueId]);

  const gradientPrefix = `sankey-${diagramId}`;

  const sankeyDiagramOptionsState = useSelector(
    (state: RootState) => state.sankeyDiagramOptions
  );

  const { maxDepth, width, expandedOthers, expandedGroups } =
    sankeyDiagramOptionsState;

  /* Apply treatment-group mapping first, then the "Other" grouping. */
  const groupMappedData = useMemo(() => {
    if (!originalData?.nodes || !originalData?.links)
      return originalData;
    return applyTreatmentGroupMapping(
      originalData,
      TREATMENT_GROUP_MAP,
      expandedGroups
    );
  }, [originalData, expandedGroups]);

  const processedData = useMemo(() => {
    if (!groupMappedData?.nodes || !groupMappedData?.links)
      return data;

    /*
     * Protect expanded group members from being collapsed into "Other".
     * Without this, a user who expands FCR-Benda (TL1) would immediately
     * see FCR (TL1) and R-Benda (TL1) disappear into "Other TL1" if their
     * individual volumes fall below the width threshold.
     */
    const protectedNodeNames = new Set<string>();
    if (expandedGroups.length > 0) {
      groupMappedData.nodes.forEach((node) => {
        const groupNodeName = resolveGroupNodeName(
          node.name,
          TREATMENT_GROUP_MAP
        );
        if (
          groupNodeName &&
          expandedGroups.includes(groupNodeName)
        ) {
          protectedNodeNames.add(node.name);
        }
      });
    }

    return groupSmallNodesToOther(
      groupMappedData,
      width,
      expandedOthers,
      protectedNodeNames
    );
  }, [data, groupMappedData, width, expandedOthers, expandedGroups]);

  const sankeyData = useMemo(() => {
    const sankeyGenerator = sankey<
      SankeyNode<any, any>,
      SankeyLink<any, any>
    >()
      .nodeWidth(14)
      .nodePadding(12)
      .nodeSort(null)
      .iterations(0)
      .size([dimension.width, dimension.height]);

    const { nodes, links } = processedData;
    const mutableNodes = nodes.map((d) => ({ ...d }));
    const mutableLinks = links.map((d) => ({ ...d }));

    return sankeyGenerator({
      nodes: mutableNodes,
      links: mutableLinks,
    });
  }, [processedData, dimension]);

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const handleNodeClick = (
      node: SankeyNode<any, any>
    ) => {
      const name = node.name;
      dispatch(expandGroupedNodes(name));
    };

    const handleGroupToggle = (groupNodeName: string) => {
      dispatch(toggleTreatmentGroup(groupNodeName));
    };

    createSankeyDiagram(
      svg,
      sankeyData,
      dimension,
      maxDepth,
      tooltipRef,
      gradientPrefix,
      customFeature?.value,
      handleNodeClick,
      handleGroupToggle,
      TREATMENT_GROUP_MAP,
      expandedGroups
    );
  }, [
    dispatch,
    sankeyData,
    dimension,
    gradientPrefix,
    maxDepth,
    customFeature?.value,
    expandedOthers,
    expandedGroups,
  ]);

  return (
    <>
      <ChartTooltip ref={tooltipRef} />
      <svg
        ref={svgRef}
        className={gradientPrefix}
        data-diagram-id={gradientPrefix}
        width={dimension.width}
      />
    </>
  );
};

export default SankeyDiagram;
